import { type PayloadAction, createSlice } from "@reduxjs/toolkit";
import { SteeringPosition, type CarType, initialExtraBase } from "../../../../shared-logic/types/CarType";
import { CERTIFICATION_NONE, getDefaultPriceObject } from "../../../../../common-deprecated/constants";
import type { CarConfigStateType } from "../store";

export const initialState: { initialized: boolean } & CarType = {
    initialized: false,
    id: "",
    shortId: 0,
    name: "",
    certification: CERTIFICATION_NONE,
    price: { ...getDefaultPriceObject() },
    efficiencyClass: "",
    availableForOnlineSales: false,
    availableForOnlineReservation: false,
    availableForOnlineLeasing: false,
    availability: {
        showAccessoriesRateCarConfig: false,
        showAnnualInterestRate: false,
        showEffectiveInterestRate: false,
        showFinanceButton: false,
        showInsuranceButton: false,
        showMonthlyRate: false,
        showProductName: false,
        showTerm: false,
        showInsurancePremiumCarConfig: false,
    },
    model: {
        id: "",
        name: "",
        webtitleSectionName: "",
        configVersion: "",
        internalCode: "",
        defaultCCISBackground: "",
        labels: [],
        defaultCar: "",
        steering: SteeringPosition.Left,
    },
    submodel: null,
    grade: {
        id: "",
        name: "",
        webtitleSectionName: "",
    },
    body: {
        id: "",
        name: "",
        webtitleSectionName: "",
    },
    motorization: {
        engine: {
            id: "",
            name: "",
            hybrid: false,
            electric: false,
            fuelCell: false,
            plugin: false,
            internalCode: "",
            type: "",
            typeId: "",
            typeCode: "",
        },
        transmission: {
            id: "",
            internalCode: "",
            name: "",
        },
        wheeldrive: {
            id: "",
            internalCode: "",
            name: "",
            description: "",
        },
    },
    colour: {
        id: "",
        shortId: "",
        code: "",
        internalCode: "",
        name: "",
        type: "",
        price: { ...getDefaultPriceObject() },
        rgb: "",
        standard: false,
        image: "",
        upholsteries: [],
        upholsteriesCarConfigurator: [],
        upholsteryOnlineAvailability: [],
        description: null,
    },
    upholstery: {
        id: "",
        objectType: "upholstery",
        shortId: "",
        internalCode: "",
        name: "",
        price: { ...getDefaultPriceObject() },
        default: false,
        standard: false,
        image: "",
        description: null,
        index: 0,
        sortIndex: 0,
        availableForColours: [],
    },
    wheel: {
        ...initialExtraBase,
        objectType: "wheel",
        isWheel: true,
        setItems: [],
    },
    packs: [],
    financePacks: [],
    extras: [],
    eco: [],
    serviceProducts: [],
    winterWheel: null,
    manualPromotions: [],
    visibleInTopSpin: false,
};

export type CarConfigCarActionTypes = ReturnType<typeof initCarConfigCar | typeof setMotorization | typeof setCarGrade>;

export const CarConfigCarSlice = createSlice({
    name: "car",
    initialState,
    reducers: {
        initCarConfigCar(state, action: PayloadAction<CarType>) {
            return {
                ...state,
                initialized: true,
                ...action.payload,
            };
        },
        setMotorization(state, action: PayloadAction<CarType["motorization"]>) {
            const { ...motorization } = action.payload;
            return {
                ...state,
                motorization,
            };
        },
        setCarGrade(state, action: PayloadAction<CarType["grade"]>) {
            return {
                ...state,
                grade: action.payload,
            };
        },
    },
});

export const { initCarConfigCar, setMotorization, setCarGrade } = CarConfigCarSlice.actions;

export const selectCarMotorization = (state: CarConfigStateType): CarType["motorization"] => state.car.motorization;

export const selectCarGrade = (state: CarConfigStateType): CarType["grade"] => state.car.grade;
